import useTabStore from "@/stores/useTabStore";
import toast from "react-hot-toast";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import {
  useSettingsStore,
  FontSize,
  Theme,
  ImportantHeadlineColorKey,
  importantHeadlineColorPresets,
} from "@/stores/SettingsStore";
import clsx from "clsx";
import { MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { useVersionState } from "@/hooks/useVersionState";

const fontSizeOptions: { value: FontSize; label: string; isDefault?: boolean }[] = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium", isDefault: true },
  { value: "large", label: "Large" },
  { value: "extra-large", label: "Extra Large" },
  { value: "extra-extra-large", label: "Extra Extra Large" },
];

function General() {
  const resetToDefaults = useTabStore((state) => state.resetToDefaults);
  const fontSize = useSettingsStore((state) => state.fontSize);
  const setFontSize = useSettingsStore((state) => state.setFontSize);
  const theme = useSettingsStore((state) => state.theme);
  const setTheme = useSettingsStore((state) => state.setTheme);
  const importantHeadlineColor = useSettingsStore(
    (state) => state.importantHeadlineColor
  );
  const setImportantHeadlineColor = useSettingsStore(
    (state) => state.setImportantHeadlineColor
  );
  const legacyViewEnabled = useSettingsStore(
    (state) => state.legacyViewEnabled
  );
  const setLegacyViewEnabled = useSettingsStore(
    (state) => state.setLegacyViewEnabled
  );
  const version = useVersionState();

  const confirmDialog = useConfirm();

  const resetTabLayouts = async () => {
    const ok = await confirmDialog({ title: "Reset Tab Layouts", message: "This will reset all tabs to their default layouts and widget configurations. This action cannot be undone.", confirmLabel: "Reset", variant: "warning" });
    if (!ok) return;
    resetToDefaults();
    toast.success("Tab layouts have been reset to defaults!");
  };

  const handleClearLocalStorage = async () => {
    const ok = await confirmDialog({ title: "Clear Local Storage", message: "This will clear all tab and other configurations stored in local storage. You may need to reload the page.", confirmLabel: "Clear", variant: "danger" });
    if (!ok) return;
    localStorage.clear();
    toast.success("Local storage has been cleared!");
  };

  return (
    <div className="divide-y divide-gray-200 dark:divide-white/10">
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Font Size</h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Adjust the size of text throughout the application. Your preference
            is saved automatically.
          </p>
        </div>
        <div className="self-center">
          <span className="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setFontSize("small")}
              className={clsx(
                "relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-r-none",
                fontSize === "small"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Small
            </button>
            <button
              type="button"
              onClick={() => setFontSize("medium")}
              className={clsx(
                "relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-none",
                fontSize === "medium"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Medium{" "}
              <span className="ml-1.5 text-xs">(Default)</span>
            </button>
            <button
              type="button"
              onClick={() => setFontSize("large")}
              className={clsx(
                "relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-none",
                fontSize === "large"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Large
            </button>
            <button
              type="button"
              onClick={() => setFontSize("extra-large")}
              className={clsx(
                "relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-none whitespace-nowrap",
                fontSize === "extra-large"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Extra Large
            </button>
            <button
              type="button"
              onClick={() => setFontSize("extra-extra-large")}
              className={clsx(
                "relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-l-none whitespace-nowrap",
                fontSize === "extra-extra-large"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Extra Extra Large
            </button>
          </span>
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Appearance</h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Choose between dark and light mode. Your preference is saved
            automatically.
          </p>
        </div>
        <div className="self-center">
          <span className="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={clsx(
                "relative inline-flex items-center gap-2 rounded-l-md px-4 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-r-none",
                theme === "dark"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              <MoonIcon className="h-4 w-4" />
              Dark
            </button>
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={clsx(
                "relative -ml-px inline-flex items-center gap-2 rounded-r-md px-4 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-l-none",
                theme === "light"
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              <SunIcon className="h-4 w-4" />
              Light
            </button>
          </span>
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Important Headlines Colouring
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Choose the colour scheme for important headlines.
          </p>
        </div>
        <div className="self-center">
          <Listbox
            value={importantHeadlineColor}
            onChange={setImportantHeadlineColor}
          >
            <div className="relative w-[32rem]">
              <ListboxButton className="relative w-full cursor-pointer rounded-md py-2 pl-3 pr-10 text-left text-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-squawk-blue">
                <span
                  className="flex items-center gap-3"
                >
                  <span
                    className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold"
                    style={{
                      backgroundColor:
                        importantHeadlineColorPresets[importantHeadlineColor].bg,
                      color:
                        importantHeadlineColorPresets[importantHeadlineColor]
                          .text,
                    }}
                  >
                    12:09 IMPORTANT HEADLINE
                  </span>
                  <span className="truncate">
                    {
                      importantHeadlineColorPresets[importantHeadlineColor]
                        .label
                    }
                  </span>
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </ListboxButton>

              <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-sm shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none">
                {(
                  Object.keys(importantHeadlineColorPresets) as ImportantHeadlineColorKey[]
                ).map((key) => {
                  const preset = importantHeadlineColorPresets[key];
                  return (
                    <ListboxOption
                      key={key}
                      value={key}
                      className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 dark:text-white data-[focus]:bg-squawk-blue data-[focus]:text-white"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold"
                          style={{
                            backgroundColor: preset.bg,
                            color: preset.text,
                          }}
                        >
                          12:09 IMPORTANT HEADLINE
                        </span>
                        <span className="truncate font-normal group-data-[selected]:font-semibold">
                          {preset.label}
                        </span>
                      </span>

                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-squawk-blue group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                        <CheckIcon className="h-5 w-5" />
                      </span>
                    </ListboxOption>
                  );
                })}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Clear local storage
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            This will clear all tab and other configurations stored in local
            storage.
          </p>
        </div>
        <div className="self-center">
          <button
            onClick={handleClearLocalStorage}
            className="text-sm px-3 py-2 rounded-md border border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:border-red-400 dark:hover:border-red-500 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          >
            Clear local storage
          </button>
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Reset layouts to defaults
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            This will reset all tabs to their default layouts and widget
            configurations. This action will not affect any other settings or
            storage.
          </p>
        </div>
        <div className="self-center">
          <button
            onClick={resetTabLayouts}
            className="text-sm px-3 py-2 rounded-md border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            Reset Tab Layouts
          </button>
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Enable Legacy View
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Show the Legacy View shortcut in the sidebar — the classic
            light-mode headline feed for users who prefer the old layout. This
            only affects your own sidebar.
          </p>
        </div>
        <div className="self-center">
          <span className="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setLegacyViewEnabled(true)}
              className={clsx(
                "relative inline-flex items-center rounded-l-md px-4 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-r-none",
                legacyViewEnabled
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Enabled
            </button>
            <button
              type="button"
              onClick={() => setLegacyViewEnabled(false)}
              className={clsx(
                "relative -ml-px inline-flex items-center rounded-r-md px-4 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 rounded-l-none",
                !legacyViewEnabled
                  ? "bg-squawk-blue hover:bg-squawk-blue-dark-soft ring-squawk-blue/30 text-white"
                  : "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-white"
              )}
            >
              Disabled
            </button>
          </span>
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Version</h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Current application version and update status.
          </p>
        </div>
        <div className="self-center space-y-3">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-gray-900 dark:text-white">
              {version.currentVersion}
            </span>
            <span className="font-mono text-xs text-gray-400">
              ({version.currentSha === "local" ? "local" : version.currentSha.slice(0, 7)})
            </span>
            {version.isUpdateAvailable ? (
              <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                Update available
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-300">
                Up to date
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => version.checkNow()}
              disabled={version.isChecking}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={clsx("h-4 w-4 transition-transform", version.isChecking && "animate-spin")} />
              {version.isChecking ? "Checking..." : "Check for updates"}
            </button>
            {version.isUpdateAvailable && (
              <button
                onClick={() => window.location.reload()}
                className="text-sm px-3 py-1.5 rounded-md bg-squawk-blue hover:bg-squawk-blue-dark-soft active:scale-95 text-white font-medium transition-all"
              >
                Reload to update
              </button>
            )}
            {!version.isChecking && version.lastChecked && (
              <span className="text-xs text-gray-400">
                Checked {formatTimeAgo(version.lastChecked)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const clearLocalStorage = () => {
  localStorage.clear();
  alert("Local Storage has been cleared!");
};

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

export default General;
